// IndexedDB works on the totem's HTTP LAN origin (unlike Service Workers).
// Store compressed bytes only: warming never creates Image/bitmap/WebGL textures.
const LIMIT = 128 * 1024 * 1024;
const MAX_ITEM = 12 * 1024 * 1024;
const TTL = 365 * 24 * 60 * 60 * 1000;
type Entry = { url: string; blob: Blob; touched: number; expires: number; bytes: number };
let database: Promise<IDBDatabase> | undefined;
let writes = Promise.resolve();
// Reuse a few thumbnail URL identities across card openings, avoiding a new
// decoded-image cache key every time. Never retain panorama URLs in this pool.
const thumbnails = new Map<string, { url: string; refs: number; touched: number }>();
export async function leaseThumbnailUrl(source: string, signal: AbortSignal): Promise<{ url: string; release: () => void }> {
  if (!managed(source)) {
    const blob = await cachedMediaBlob(source, signal);
    if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
    const url = URL.createObjectURL(blob);
    return { url, release: () => URL.revokeObjectURL(url) };
  }
  let entry = thumbnails.get(source);
  if (!entry) {
    const blob = await cachedMediaBlob(source, signal);
    if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
    entry = thumbnails.get(source);
    if (!entry) {
      entry = { url: URL.createObjectURL(blob), refs: 0, touched: Date.now() };
      thumbnails.set(source, entry);
    }
  }
  entry.refs++; entry.touched = Date.now();
  let released = false;
  return { url: entry.url, release: () => {
    if (released) return;
    released = true; entry!.refs--; entry!.touched = Date.now();
    const unused = [...thumbnails.entries()].filter(([, value]) => !value.refs)
      .sort((a, b) => a[1].touched - b[1].touched);
    // At most four small decoded thumbnail identities remain between visits.
    while (unused.length > 4) {
      const [key, old] = unused.shift()!;
      URL.revokeObjectURL(old.url); thumbnails.delete(key);
    }
  } };
}
const managed = (url: string) => /\/storage\/optimized-media\/[a-f0-9]{64}\.webp$/.test(new URL(url, location.href).pathname);
export const mediaApiUrl = (url: string) => url.startsWith('/') ? `${import.meta.env.VITE_API_URL ?? ''}${url}` : url;

function open(): Promise<IDBDatabase> {
  if (!database) database = new Promise((resolve, reject) => {
    const request = indexedDB.open('tourism-media-v1', 1);
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore('media', { keyPath: 'url' });
      store.createIndex('touched', 'touched');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Media cache blocked'));
  }).catch((error) => { database = undefined; throw error; });
  return database;
}

async function read(url: string): Promise<Blob | null> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('media', 'readwrite');
    const store = tx.objectStore('media');
    const request = store.get(url);
    let blob: Blob | null = null;
    request.onsuccess = () => {
      const entry = request.result as Entry | undefined;
      if (entry && entry.expires > Date.now()) {
        blob = entry.blob;
        store.put({ ...entry, touched: Date.now() });
      } else if (entry) store.delete(url);
    };
    tx.oncomplete = () => resolve(blob);
    tx.onabort = tx.onerror = () => reject(tx.error);
  });
}

async function write(url: string, blob: Blob): Promise<void> {
  if (blob.size > MAX_ITEM) return;
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('media', 'readwrite');
    const store = tx.objectStore('media');
    const entries: Array<{ url: string; bytes: number }> = [];
    let total = blob.size;
    const cursor = store.index('touched').openCursor();
    cursor.onsuccess = () => {
      const current = cursor.result;
      if (current) {
        const entry = current.value as Entry;
        if (entry.url === url || entry.expires <= Date.now()) current.delete();
        else { total += entry.bytes; entries.push({ url: entry.url, bytes: entry.bytes }); }
        current.continue();
      } else {
        for (const entry of entries) {
          if (total <= LIMIT) break;
          store.delete(entry.url); total -= entry.bytes;
        }
        store.put({ url, blob, bytes: blob.size, touched: Date.now(), expires: Date.now() + TTL } satisfies Entry);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error);
  });
}

export async function cachedMediaBlob(url: string, signal: AbortSignal): Promise<Blob> {
  if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
  if (managed(url)) {
    const hit = await read(url).catch(() => null);
    if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (hit) { console.debug('[media-cache] hit', url); return hit; }
  }
  const response = await fetch(url, { signal, cache: 'default' });
  if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) throw new Error(`Media HTTP ${response.status}`);
  const blob = await response.blob();
  if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
  if (managed(url)) {
    writes = writes.catch(() => {}).then(() => write(url, blob)).catch(() => {
      console.warn('[media-cache] Storage unavailable; using HTTP/network fallback');
    });
    await writes;
  }
  return blob;
}

export async function warmMedia(urls: string[], signal: AbortSignal): Promise<void> {
  for (const url of new Set(urls)) {
    if (signal.aborted) return;
    if (!managed(url)) continue;
    // One request at a time; a short yield avoids monopolizing the WebView.
    await new Promise((resolve) => setTimeout(resolve, 250));
    if (signal.aborted) return;
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 15_000);
    try { await cachedMediaBlob(url, controller.signal); }
    catch { /* Foreground load retries; never cache failures. */ }
    finally { clearTimeout(timeout); signal.removeEventListener('abort', cancel); }
  }
}
